import { Injectable } from "@angular/core";
import { Action, State, StateContext, Store } from "@ngxs/store";
import { map, tap } from "rxjs/operators";
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { InsightConfigurationType, KpiList } from "../../models/insight-configuration.model";
import { InsightConfigurationService } from "../../services/insight-configuration.service";
import { ClearKpiConfigurationState, GetKpiList, RemoveSelectedKPI, ResetCreateConfigStatus, SetConfiguredKpiList, GetEditKPIConfiguration, SetKPIMode, SetSelectedKPIs, SubmitKPIConfiguration, UpdateKPIActionIn, SaveEditKPIConfiguration } from "./kpi-configuration.action";

/* istanbul ignore file */
export class KPIConfigurationStateModel {
    kpiListSelection: KpiList[];
    configuredKpiList: KpiList[];
    kpisSelected: any[];
    configurationType: InsightConfigurationType;
    createConfigSuccess: boolean;
    createConfigError: any;
    editConfiguration: any;
    editConfigurationId: string;
    editConfigurationCategory: string;
    mode: number;
}

@State<KPIConfigurationStateModel>({
    name: 'kpi',
    defaults: {
        kpiListSelection: [],
        configuredKpiList: [],
        kpisSelected: [],
        configurationType: null,
        createConfigSuccess: null,
        createConfigError: null,
        editConfiguration: [],
        editConfigurationId: null,
        editConfigurationCategory: null,
        mode: null
    }
})
@Injectable()
export class KPIConfigurationState{

    constructor(
      private insightConfiguratonService: InsightConfigurationService,
      private store: Store
    ) {}

    @Action(GetKpiList, {cancelUncompleted: true})
    getKpiList(ctx: StateContext<KPIConfigurationStateModel>) {
        const state = ctx.getState();
        let plant = this.store.selectSnapshot(state => state.configurations.selectedPlant);

        return this.insightConfiguratonService.getKpiValidationList(plant.plantId)
        .pipe(
            tap((kpiList: any) => {
            const newKpiList = kpiList.map(kpi => {
                kpi.selected = false;
                kpi.actionIn = {value: 1, unit: 'Day'}
                return kpi;
            })
            ctx.patchState({
                ...state,
                kpiListSelection: [...newKpiList]
            })
            })
        )
    }

    @Action(ClearKpiConfigurationState, {cancelUncompleted: true})
    clearKpiList(ctx: StateContext<KPIConfigurationStateModel>) {
        ctx.patchState({
            kpiListSelection: [],
            kpisSelected: []
        })
    }

    @Action(SetSelectedKPIs, {cancelUncompleted: true})
    setSelectedKPIs(ctx: StateContext<KPIConfigurationStateModel>, {selectedKPIs}: SetSelectedKPIs) {
      const state = ctx.getState();
      let error = state.createConfigError;
      if(error !== null){
        selectedKPIs = selectedKPIs.map(kpi => {
          return kpi;
        });
      }
      ctx.patchState({
        kpisSelected: selectedKPIs
      })
    }

    @Action(RemoveSelectedKPI)
    removeSelectedKPI(ctx: StateContext<KPIConfigurationStateModel>,{selectedKPI}: RemoveSelectedKPI) {
      ctx.setState(
        patch({
          kpisSelected : removeItem<any>(kpi => (kpi?.insightCategoryParameters?.uid || kpi?.uid) === selectedKPI?.uid ),
          kpiListSelection:updateItem<any> (kpi => (kpi?.insightCategoryParameters?.uid || kpi?.uid) === selectedKPI?.uid, patch({selected: false}) )
        })
      )
    }

    @Action(UpdateKPIActionIn)
    updateKPIActionIn(ctx: StateContext<KPIConfigurationStateModel>, {selectedKPI}: UpdateKPIActionIn) {
      ctx.setState(
        patch({
          kpisSelected: updateItem<any>(kpi => kpi.uid === selectedKPI.insightCategoryParameters.uid,
            patch({actionIn: selectedKPI.actionIn})
            )
        })
      )
    }

    @Action(SubmitKPIConfiguration, {cancelUncompleted: true})
    submitKPIConfiguration(ctx: StateContext<KPIConfigurationStateModel>, {category, selectedKpis}: SubmitKPIConfiguration) {
      return this.insightConfiguratonService.submitKpiConfiguration(category ,selectedKpis)
      .pipe(
        tap((data) => {
          if(data['insightConfiguration']){
            // Patch state for success scenario
            ctx.patchState({
              createConfigSuccess: true,
              createConfigError: null
            })
          }else if(data['error']) {
            // Patch state for known error scenario
            ctx.patchState({
              createConfigSuccess: false,
              createConfigError: data
            })
          } else {
            // Patch state for generic / server error scenario
            ctx.patchState({
              createConfigSuccess: false,
              createConfigError: null
            })
          }
        })
      )
    }

    @Action(ResetCreateConfigStatus)
    resetCreateConfigStatus(ctx: StateContext<KPIConfigurationStateModel>) {

      // Reset the create config status values to null
      ctx.patchState({
        createConfigError: null,
        createConfigSuccess: null
      })
    }

    @Action(SetConfiguredKpiList, {cancelUncompleted: true})
    setConfiguredKpiList(ctx: StateContext<KPIConfigurationStateModel>, {configuredKPIs}: SetConfiguredKpiList) {
      const state = ctx.getState();
      // Patch state for configured KPI list
      ctx.patchState({
        ...state,
        configuredKpiList: configuredKPIs
      })
    }

    @Action(SetKPIMode)
    setKPIMode(ctx: StateContext<KPIConfigurationStateModel>, {mode}: SetKPIMode){
      ctx.patchState({
        mode: mode
      })
    }

    @Action(GetEditKPIConfiguration)
    getEditKPIConfiguration(ctx: StateContext<KPIConfigurationStateModel>, {configId}: GetEditKPIConfiguration){
      return this.insightConfiguratonService.getConfigurationDetails(configId)
      .pipe(
          map((KPI: any) => {
            if(KPI !== null) {
              ctx.patchState({
                editConfigurationCategory: KPI.configuration[0]?.category,
                editConfigurationId: KPI.uid,
                editConfiguration: KPI.configuration[0]
              })
            }
          })
      )
    }

    @Action(SaveEditKPIConfiguration, {cancelUncompleted: true})
    saveEditKPIConfiguration(ctx: StateContext<KPIConfigurationStateModel>, {configId, category, editKPI}: SaveEditKPIConfiguration) {
      return this.insightConfiguratonService.saveEditKpiConfiguration(configId, category, editKPI);
    }
}
